import { OVERVIEW_PERMISSIONS, Permission as P, REVIEW_PERMISSIONS } from '@construction/shared';
import type { RouteDef, Variant } from './types';

/**
 * 全部路由（CR-040）。**入口、守卫、页面、加载的接口只在这里写一次。**
 *
 * 读法：
 * - `variants` 从上往下，第一个过门槛的生效；一个都过不了就是没权限（跳回落脚点，不解释 ——
 *   CR-027：这不是他该看到的页面，多一句「无权限」没有意义）
 * - `entries` 是这个页面在哪里有入口。不写 gate 就跟守卫一样
 * - `loads` 是页面一打开就会调的接口；`gated` 是页面里按权限才出现的查询和按钮
 *
 * 改了这张表之后跑 `registry.test.ts`：它对所有权限组合 × 两套外壳检查
 * 「能进的页面都有入口」「入口都进得去」「页面调的接口都有权限」「落脚点能进」。
 *
 * **外壳（网页样式 / App 样式）由设备决定，和这张表无关**（客户决定，CR-040）。
 *
 * **一个模块一页**（CR-042）：日报、隐蔽工程、采购各只有一条路由，页面里按权限出
 * 填写按钮和「我的 / 本工地」两个 tab。改之前「我的 X」和「X」是两条路由，菜单里占两项。
 * CR-044 起图纸和进度表在 App 里也是这样：和网页端同一页，上传按钮按权限出现（没有「资料」「管理图纸」了）。
 *
 * **App 底部栏**（CR-044）：首页 / 日报 / 隐蔽工程 / 采购 / 我的，中间三格有权限才出现；其余都是首页卡片。
 * **App 返回键**：底部栏以外的每条 App 路由都写 `parent`，由 MobileLayout 统一画（`registry.test.ts` 查着）。
 */

// ---------------------------------------------------------------- 页面变体（两套外壳共用的写法）

/** 日报：填写按钮 + 我的 / 本工地（CR-042） */
const DAILY_LOGS: Variant = {
  gate: { anyOf: [P.DAILY_LOG_WRITE, P.DAILY_LOG_VIEW] },
  page: 'DailyLogsPage',
  loads: [],
  gated: [
    { api: 'GET me/daily-logs', gate: { anyOf: [P.DAILY_LOG_WRITE] }, what: '「我的」tab' },
    { api: 'POST daily-logs', gate: { anyOf: [P.DAILY_LOG_WRITE] }, what: '写今日日报' },
    { api: 'POST files/presign', gate: { anyOf: [P.DAILY_LOG_WRITE] }, what: '传照片视频' },
    { api: 'GET sites/:siteId/daily-logs', gate: { anyOf: [P.DAILY_LOG_VIEW] }, what: '「本工地」tab' },
    { api: 'GET sites/:siteId/exports/daily-logs', gate: { anyOf: [P.DAILY_LOG_EXPORT] }, what: '下载（CR-043）' },
  ],
};

/** 采购：记一笔 + 我的 / 本工地（CR-042） */
const PURCHASES: Variant = {
  gate: { anyOf: [P.PURCHASE_WRITE, P.PURCHASE_VIEW] },
  page: 'PurchasesPage',
  loads: [],
  gated: [
    { api: 'GET me/purchases', gate: { anyOf: [P.PURCHASE_WRITE] }, what: '「我的」tab' },
    { api: 'GET sites/:siteId/purchases/categories', gate: { anyOf: [P.PURCHASE_WRITE] }, what: '记一笔的类别下拉' },
    { api: 'POST purchases', gate: { anyOf: [P.PURCHASE_WRITE] }, what: '提交采购' },
    { api: 'POST files/presign', gate: { anyOf: [P.PURCHASE_WRITE] }, what: '传小票' },
    { api: 'GET sites/:siteId/purchases', gate: { anyOf: [P.PURCHASE_VIEW] }, what: '「本工地」tab' },
    { api: 'GET sites/:siteId/purchases/summary', gate: { anyOf: [P.PURCHASE_VIEW] }, what: '「本工地」的汇总与分桶计数' },
    { api: 'GET sites/:siteId/exports/purchases', gate: { anyOf: [P.PURCHASE_EXPORT] }, what: '下载（CR-043）' },
    { api: 'POST purchases/:id/review', gate: { anyOf: [P.PURCHASE_REVIEW] }, what: '批准 / 驳回 / 报销、批量报销' },
  ],
};

/** 隐蔽工程：拍隐蔽工程 + 我的 / 本工地（CR-042） */
const HIDDEN_WORKS: Variant = {
  gate: { anyOf: [P.HIDDEN_WORK_WRITE, P.HIDDEN_WORK_VIEW] },
  page: 'HiddenWorksPage',
  loads: [],
  gated: [
    { api: 'GET me/hidden-works', gate: { anyOf: [P.HIDDEN_WORK_WRITE] }, what: '「我的」tab' },
    { api: 'GET sites/:siteId/hidden-works/categories', gate: { anyOf: [P.HIDDEN_WORK_WRITE, P.HIDDEN_WORK_VIEW] }, what: '类别下拉（表单和筛选）' },
    { api: 'POST hidden-works', gate: { anyOf: [P.HIDDEN_WORK_WRITE] }, what: '提交（只在 App 样式里）' },
    { api: 'POST files/presign', gate: { anyOf: [P.HIDDEN_WORK_WRITE] }, what: '传照片' },
    { api: 'GET sites/:siteId/hidden-works', gate: { anyOf: [P.HIDDEN_WORK_VIEW] }, what: '「本工地」tab' },
    { api: 'GET sites/:siteId/member-options', gate: { anyOf: [P.HIDDEN_WORK_VIEW] }, what: '「本工地」按拍摄人筛' },
    { api: 'GET sites/:siteId/exports/hidden-works', gate: { anyOf: [P.HIDDEN_WORK_EXPORT] }, what: '下载（CR-043）' },
  ],
};

const DRAWINGS_ADMIN: Variant = {
  gate: { anyOf: [P.DRAWING_VIEW] },
  page: 'DrawingsPage',
  loads: ['GET sites/:siteId/drawings?kind=DRAWING'],
  gated: [
    { api: 'GET drawings/:id', gate: { anyOf: [P.DRAWING_VIEW] }, what: '点开看图' },
    { api: 'POST sites/:siteId/drawings?kind=DRAWING', gate: { anyOf: [P.DRAWING_UPLOAD] }, what: '上传' },
    { api: 'POST files/presign', gate: { anyOf: [P.DRAWING_UPLOAD] }, what: '上传文件' },
    { api: 'POST drawings/:id/pins', gate: { anyOf: [P.DRAWING_UPLOAD] }, what: '加图钉' },
    { api: 'DELETE drawings/pins/:pinId', gate: { anyOf: [P.DRAWING_UPLOAD] }, what: '删图钉' },
    { api: 'GET sites/:siteId/exports/drawings', gate: { anyOf: [P.DRAWING_EXPORT] }, what: '下载全部（CR-043）' },
    { api: 'GET drawings/:id/download?kind=DRAWING', gate: { anyOf: [P.DRAWING_EXPORT] }, what: '下载单张' },
  ],
};

const SCHEDULE_PAGE: Variant = {
  gate: { anyOf: [P.SCHEDULE_VIEW] },
  page: 'SchedulePage',
  // 原件 + 解析核对过的数据（CR-080，甘特图按它画）
  loads: ['GET sites/:siteId/drawings?kind=SCHEDULE', 'GET sites/:siteId/schedule'],
  gated: [
    { api: 'POST sites/:siteId/drawings?kind=SCHEDULE', gate: { anyOf: [P.SCHEDULE_UPLOAD] }, what: '换进度表' },
    { api: 'PUT sites/:siteId/schedule', gate: { anyOf: [P.SCHEDULE_UPLOAD] }, what: '核对、发布甘特图（CR-080）' },
    { api: 'POST files/presign', gate: { anyOf: [P.SCHEDULE_UPLOAD] }, what: '上传文件' },
    { api: 'GET sites/:siteId/exports/schedule', gate: { anyOf: [P.SCHEDULE_EXPORT] }, what: '下载全部版本（CR-043）' },
    { api: 'GET drawings/:id/download?kind=SCHEDULE', gate: { anyOf: [P.SCHEDULE_EXPORT] }, what: '下载当前' },
  ],
};

const ATTENDANCE_PAGE: Variant = {
  gate: { anyOf: [P.ATTENDANCE_VIEW] },
  page: 'AttendancePage',
  // 页签上的数字（待复核 / 异常，CR-052）进页就要，只看考勤的人也看得到
  loads: ['GET sites/:siteId/attendance/summary', 'GET sites/:siteId/attendance/exceptions'],
  gated: [
    { api: 'GET sites/:siteId/punches', gate: { anyOf: [P.ATTENDANCE_VIEW] }, what: '展开某人的打卡明细' },
    { api: 'POST punches/:id/review', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '复核' },
    { api: 'POST punches/manual', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '补录打卡' },
    { api: 'POST punches/:id/anomaly-resolution', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '异常标记已处理（CR-052）' },
    { api: 'DELETE punches/:id/anomaly-resolution', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '撤销异常已处理（CR-052）' },
    { api: 'GET sites/:siteId/attendance/backfill-context', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '补卡建议（他平时几点、这天的卡）' },
    { api: 'GET sites/:siteId/member-options', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '补录选人' },
    { api: 'GET sites/:siteId/exports/attendance', gate: { anyOf: [P.ATTENDANCE_EXPORT] }, what: '下载考勤表（CR-043）' },
  ],
};

const SITE_SETTINGS: Variant = {
  gate: { anyOf: [P.SITE_MANAGE] },
  page: 'SiteDetailPage',
  loads: ['GET sites/:id', 'GET sites/:siteId/members'],
  gated: [
    { api: 'PATCH sites/:id', gate: { anyOf: [P.SITE_MANAGE] }, what: '改工地设置' },
    { api: 'GET users', gate: { anyOf: [P.SITE_MANAGE] }, what: '加成员选人' },
    { api: 'POST sites/:siteId/members', gate: { anyOf: [P.SITE_MANAGE] }, what: '加成员' },
    { api: 'GET geocode/search', gate: { anyOf: [P.SITE_MANAGE] }, what: '地址搜索' },
  ],
};

const SITES_PAGE: Variant = {
  gate: {},
  page: 'SitesPage',
  loads: ['GET sites'],
  gated: [
    { api: 'POST sites', gate: { anyOf: [P.SITE_CREATE] }, what: '建工地' },
    { api: 'GET geocode/search', gate: { anyOf: [P.SITE_CREATE] }, what: '地址搜索' },
    { api: 'GET orgs', gate: {}, what: '平台账号按公司筛、建工地选公司（CR-073）' },
  ],
};

const USERS_PAGE: Variant = {
  gate: { anyOf: [P.USER_MANAGE, P.SITE_MANAGE] },
  page: 'UsersPage',
  loads: ['GET users'],
  gated: [
    // 平台账号的「公司」列、筛选、建账号的下拉（CR-073）。单位账号不调
    { api: 'GET orgs', gate: {}, what: '按公司筛、建账号选公司' },
    { api: 'GET roles', gate: { anyOf: [P.USER_MANAGE] }, what: '角色下拉' },
    { api: 'POST users', gate: { anyOf: [P.USER_MANAGE] }, what: '建账号' },
    { api: 'PATCH users/:id', gate: { anyOf: [P.USER_MANAGE] }, what: '改账号' },
    { api: 'POST users/:id/reset-password', gate: { anyOf: [P.USER_MANAGE] }, what: '重置密码' },
    { api: 'POST users/:id/set-password', gate: { anyOf: [P.USER_MANAGE] }, what: '设置指定密码（CR-077）' },
    // 编辑 / 建账号时直接勾所在工地（CR-071）：动的是成员关系，门槛和工地设置里加人一样
    { api: 'GET sites', gate: { anyOf: [P.SITE_MANAGE] }, what: '勾工地的列表' },
    { api: 'GET users/:id/sites', gate: { anyOf: [P.SITE_MANAGE] }, what: '回显所在工地' },
    { api: 'PUT users/:id/sites', gate: { anyOf: [P.SITE_MANAGE] }, what: '指定所在工地' },
  ],
};

/**
 * 角色页（CR-073 起不只超管）：有 role.manage 的人建、改、删**本单位**的角色，
 * 共享角色只有超管能改 —— 每一行能不能动问 shared 的 `canEditRole`
 */
const ROLES_PAGE: Variant = {
  gate: { anyOf: [P.ROLE_MANAGE] },
  page: 'RolesPage',
  loads: ['GET roles'],
  gated: [
    { api: 'POST roles', gate: { anyOf: [P.ROLE_MANAGE] }, what: '建角色' },
    { api: 'PATCH roles/:id', gate: { anyOf: [P.ROLE_MANAGE] }, what: '改角色' },
    { api: 'DELETE roles/:id', gate: { anyOf: [P.ROLE_MANAGE] }, what: '删角色' },
    { api: 'GET orgs', gate: {}, what: '平台账号建角色选公司（CR-073）' },
    { api: 'GET users', gate: { anyOf: [P.USER_MANAGE, P.SITE_MANAGE] }, what: '查看挂在这个职位上的人（CR-075）' },
  ],
};

/** 单位（CR-073：公司 / 分公司）。开、改名、归档只有超管 */
const ORGS_PAGE: Variant = {
  gate: { system: true },
  page: 'OrgsPage',
  loads: ['GET orgs'],
  gated: [
    { api: 'POST orgs', gate: { system: true }, what: '开新单位' },
    { api: 'PATCH orgs/:id', gate: { system: true }, what: '改名、归档' },
  ],
};

const AUDIT_PAGE: Variant = {
  gate: { anyOf: [P.AUDIT_VIEW] },
  page: 'AuditLogsPage',
  loads: ['GET audit-logs', 'GET audit-logs/actions', 'GET audit-logs/actors'],
  gated: [{ api: 'GET orgs', gate: {}, what: '平台账号按公司筛（CR-073）' }],
};

/**
 * 工地概览：网页端 `/sites/:siteId` 和 App 的 `/m/overview` 是同一页（CR-044）。
 * 每一格都能点进去，点的地方必须进得去（CR-039：改之前格子不看权限就摆链接）。
 * 两端门槛一样，链接指向各自外壳里的页面 —— 页面里用 `shellRoute` 取路径，这里给穷举测试查「链接进得去」。
 */
function overviewVariant(shell: 'desktop' | 'mobile'): Variant {
  const to =
    shell === 'desktop'
      ? ({ attendance: 'd.attendance', purchases: 'd.purchases', logs: 'd.logs', settings: 'd.siteSettings', hidden: 'd.hiddenWorks' } as const)
      : ({ attendance: 'm.attendance', purchases: 'm.purchases', logs: 'm.logs', settings: 'm.siteSettings', hidden: 'm.hidden' } as const);
  return {
    gate: { anyOf: OVERVIEW_PERMISSIONS },
    page: 'DashboardPage',
    loads: ['GET sites/:siteId/overview'],
    // 「今天的进度」（CR-082）：看得了进度表才查
    gated: [{ api: 'GET sites/:siteId/schedule', gate: { anyOf: [P.SCHEDULE_VIEW] }, what: '今天的进度' }],
    links: [
      {
        to: shell === 'desktop' ? 'd.schedule' : 'm.schedule',
        gate: { anyOf: [P.SCHEDULE_VIEW] },
        what: '今天的进度 · 进度表',
        expectPage: 'SchedulePage',
      },
      { to: to.attendance, gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '待复核打卡', expectPage: 'AttendancePage' },
      { to: to.purchases, gate: { anyOf: [P.PURCHASE_REVIEW] }, what: '待审批采购', expectPage: 'PurchasesPage' },
      { to: to.attendance, gate: { anyOf: [P.ATTENDANCE_VIEW] }, what: '今日在岗', expectPage: 'AttendancePage' },
      { to: to.logs, gate: { anyOf: [P.DAILY_LOG_VIEW] }, what: '今日日报', expectPage: 'DailyLogsPage' },
      { to: to.settings, gate: { allOf: [P.ATTENDANCE_VIEW, P.SITE_MANAGE] }, what: '现场人员', expectPage: 'SiteDetailPage' },
      { to: to.hidden, gate: { anyOf: [P.HIDDEN_WORK_VIEW] }, what: '近期隐蔽工程 · 查看全部', expectPage: 'HiddenWorksPage' },
    ],
  };
}

// ---------------------------------------------------------------- 路由

export const ROUTES: readonly RouteDef[] = [
  // ============================================================ 两边都有
  {
    id: 'changePassword',
    shell: 'both',
    path: '/change-password',
    siteScoped: false,
    // 挂在两套外壳之外，MobileLayout 画不到返回键，页面自己按这一项画（CR-044）
    parent: 'm.me',
    variants: [{ gate: {}, page: 'ChangePasswordPage', loads: [] }],
    entries: [
      // 改之前网页端没有这个入口，只有手机「我的」页里有
      { slot: 'account', order: 90 },
      { slot: 'meMenu', order: 90 },
    ],
  },

  // ============================================================ 网页端 · 工地之外
  {
    id: 'd.sites',
    shell: 'desktop',
    path: '/sites',
    siteScoped: false,
    landing: 900,
    twin: 'm.sites',
    variants: [SITES_PAGE],
    entries: [{ slot: 'globalNav', order: 10 }],
  },
  {
    id: 'd.users',
    shell: 'desktop',
    path: '/users',
    siteScoped: false,
    landing: 910,
    twin: 'm.users',
    variants: [USERS_PAGE],
    entries: [{ slot: 'globalNav', order: 20 }],
  },
  {
    id: 'd.roles',
    shell: 'desktop',
    path: '/roles',
    siteScoped: false,
    landing: 920,
    twin: 'm.roles',
    variants: [ROLES_PAGE],
    entries: [{ slot: 'globalNav', order: 30 }],
  },
  {
    id: 'd.audit',
    shell: 'desktop',
    path: '/audit',
    siteScoped: false,
    landing: 930,
    twin: 'm.audit',
    variants: [AUDIT_PAGE],
    entries: [{ slot: 'globalNav', order: 40 }],
  },
  {
    // 单位（CR-073）。排在「工地」前面：超管开一家新公司的顺序是 公司 → 账号 → 工地
    id: 'd.orgs',
    shell: 'desktop',
    path: '/orgs',
    siteScoped: false,
    landing: 940,
    twin: 'm.orgs',
    variants: [ORGS_PAGE],
    entries: [{ slot: 'globalNav', order: 5 }],
  },

  // ============================================================ 网页端 · 工地之内
  {
    id: 'd.overview',
    shell: 'desktop',
    path: '/sites/:siteId',
    siteScoped: true,
    landing: 10,
    twin: 'm.overview',
    variants: [overviewVariant('desktop')],
    entries: [{ slot: 'siteNav', order: 10, group: 'site' }],
  },
  {
    id: 'd.punch',
    shell: 'desktop',
    path: '/sites/:siteId/punch',
    siteScoped: true,
    twin: 'm.punch',
    // 网页端也能打卡（CR-069，客户原话「只要能开启定位就能打卡…就算无法开启也可以强制打卡，管理员复查」）：
    // 浏览器有定位 API，围栏判定本来就在服务端（约束 2），拿不到定位走「强行打卡 → 待复核」那条路。
    // 改之前这里是一张「打卡请用手机 App」的提示页（CR-040）
    variants: [
      {
        gate: { anyOf: [P.ATTENDANCE_PUNCH], notSystem: true },
        page: 'PunchPage',
        loads: ['GET me/shift', 'GET sites/:siteId/me/punches/today'],
        gated: [{ api: 'POST punches', gate: { anyOf: [P.ATTENDANCE_PUNCH] }, what: '打卡' }],
      },
    ],
    entries: [{ slot: 'siteNav', order: 30, group: 'records' }],
  },
  {
    id: 'd.logs',
    shell: 'desktop',
    path: '/sites/:siteId/logs',
    siteScoped: true,
    landing: 30,
    twin: 'm.logs',
    variants: [DAILY_LOGS],
    entries: [{ slot: 'siteNav', order: 32, group: 'records' }],
  },
  {
    id: 'd.drawings',
    shell: 'desktop',
    path: '/sites/:siteId/drawings',
    siteScoped: true,
    landing: 40,
    twin: 'm.drawings',
    variants: [DRAWINGS_ADMIN],
    entries: [{ slot: 'siteNav', order: 12, group: 'site' }],
  },
  {
    id: 'd.schedule',
    shell: 'desktop',
    path: '/sites/:siteId/schedule',
    siteScoped: true,
    landing: 41,
    twin: 'm.schedule',
    variants: [SCHEDULE_PAGE],
    entries: [{ slot: 'siteNav', order: 14, group: 'site' }],
  },
  {
    id: 'd.hiddenWorks',
    shell: 'desktop',
    path: '/sites/:siteId/hidden-works',
    siteScoped: true,
    landing: 50,
    twin: 'm.hidden',
    variants: [HIDDEN_WORKS],
    entries: [{ slot: 'siteNav', order: 34, group: 'records' }],
  },
  {
    id: 'd.purchases',
    shell: 'desktop',
    path: '/sites/:siteId/purchases',
    siteScoped: true,
    landing: 60,
    twin: 'm.purchases',
    variants: [PURCHASES],
    entries: [{ slot: 'siteNav', order: 36, group: 'records' }],
  },
  {
    id: 'd.attendance',
    shell: 'desktop',
    path: '/sites/:siteId/attendance',
    siteScoped: true,
    landing: 70,
    twin: 'm.attendance',
    variants: [ATTENDANCE_PAGE],
    entries: [{ slot: 'siteNav', order: 38, group: 'records' }],
  },
  {
    id: 'd.siteSettings',
    shell: 'desktop',
    path: '/sites/:siteId/settings',
    siteScoped: true,
    landing: 80,
    twin: 'm.siteSettings',
    variants: [SITE_SETTINGS],
    entries: [{ slot: 'siteNav', order: 16, group: 'site' }],
  },

  // ============================================================ 手机端
  {
    id: 'm.home',
    shell: 'mobile',
    path: '/m',
    siteScoped: false,
    landing: 0,
    twin: 'd.overview',
    variants: [
      {
        gate: {},
        page: 'MobileHomePage',
        loads: [],
        // 每张卡自己查自己的数，卡不出现就不查
        gated: [
          { api: 'GET sites/:siteId/me/punches/today', gate: { anyOf: [P.ATTENDANCE_PUNCH], notSystem: true }, what: '打卡卡片' },
          { api: 'GET sites/:siteId/overview', gate: { anyOf: OVERVIEW_PERMISSIONS }, what: '待办 / 今日统计' },
          { api: 'GET sites/:siteId/drawings?kind=DRAWING', gate: { anyOf: [P.DRAWING_VIEW] }, what: '图纸份数' },
          { api: 'GET sites/:siteId/schedule', gate: { anyOf: [P.SCHEDULE_VIEW] }, what: '进度表今天在做的（CR-082）' },
        ],
      },
    ],
    entries: [], // 底部栏固定的「首页」
  },
  {
    id: 'm.punch',
    shell: 'mobile',
    path: '/m/punch',
    siteScoped: false,
    twin: 'd.punch',
    parent: 'm.home',
    variants: [
      {
        // 超管隐式有打卡权限，但界面上不给超管打卡入口（CLAUDE.md 约束 7）
        gate: { anyOf: [P.ATTENDANCE_PUNCH], notSystem: true },
        page: 'PunchPage',
        loads: ['GET me/shift', 'GET sites/:siteId/me/punches/today'],
        gated: [{ api: 'POST punches', gate: { anyOf: [P.ATTENDANCE_PUNCH] }, what: '打卡' }],
      },
    ],
    // 改之前「概览版首页」没有打卡入口：既能打卡又能看考勤的人找不到打卡页
    // 首页第一张（CR-045，客户给的顺序：打卡 → 待办 → 工地概览 → 图纸 → 进度表 → 考勤）
    entries: [{ slot: 'homeCard', order: 5, card: 'punch' }],
  },
  {
    id: 'm.review',
    shell: 'mobile',
    path: '/m/review',
    siteScoped: false,
    parent: 'm.home',
    variants: [
      {
        gate: { anyOf: REVIEW_PERMISSIONS },
        page: 'ReviewPage',
        loads: [],
        gated: [
          { api: 'GET sites/:siteId/punches', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '待复核打卡' },
          { api: 'POST punches/:id/review', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '复核打卡' },
          { api: 'GET sites/:siteId/attendance/exceptions', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '考勤异常（CR-052）' },
          { api: 'GET sites/:siteId/purchases', gate: { anyOf: [P.PURCHASE_REVIEW] }, what: '待审批采购' },
          { api: 'POST purchases/:id/review', gate: { anyOf: [P.PURCHASE_REVIEW] }, what: '审批采购' },
        ],
        // 采购的「查看详情」去采购页的「本工地」tab（CR-042，`?tab=site`）
        links: [
          { to: 'm.attendance', gate: { anyOf: [P.ATTENDANCE_MANAGE] }, what: '打卡详情', expectPage: 'AttendancePage' },
          { to: 'm.purchases', gate: { anyOf: [P.PURCHASE_REVIEW] }, what: '采购详情', expectPage: 'PurchasesPage' },
        ],
      },
    ],
    // 底部栏不再有待办格（CR-044）：首页顶上的待办卡带数字，一进来就看得到
    entries: [{ slot: 'homeCard', order: 10, card: 'review' }],
  },

  // ---- 底部栏中间三格：位置固定 日报 → 隐蔽工程 → 采购，有权限才出现（CR-044）
  {
    id: 'm.logs',
    shell: 'mobile',
    path: '/m/logs',
    siteScoped: false,
    twin: 'd.logs',
    variants: [DAILY_LOGS],
    entries: [{ slot: 'tab', order: 1, tab: 'logs' }],
  },
  {
    id: 'm.hidden',
    shell: 'mobile',
    path: '/m/hidden',
    siteScoped: false,
    twin: 'd.hiddenWorks',
    variants: [HIDDEN_WORKS],
    // 甲方只看不拍也有这一格（CR-040：改之前入口只认「拍隐蔽工程」）
    entries: [{ slot: 'tab', order: 2, tab: 'hidden' }],
  },
  {
    id: 'm.purchases',
    shell: 'mobile',
    path: '/m/purchases',
    siteScoped: false,
    twin: 'd.purchases',
    variants: [PURCHASES],
    entries: [{ slot: 'tab', order: 3, tab: 'purchases' }],
  },

  // ---- 首页卡片
  {
    // 网页端工地概览那一页（CR-044，客户原话「/sites/:siteId 作为和图纸 进度表一样的放在首页」）。
    // 改之前 App 底部第一格按权限叫「工地概览」，其实点进去是首页；真正的概览在 App 里没有。
    // OVERVIEW_PERMISSIONS 含「看全工地隐蔽工程」，分包预置就有 —— 他们的概览里只有近期隐蔽工程，和网页端一致
    id: 'm.overview',
    shell: 'mobile',
    path: '/m/overview',
    siteScoped: false,
    twin: 'd.overview',
    parent: 'm.home',
    variants: [overviewVariant('mobile')],
    entries: [{ slot: 'homeCard', order: 20, card: 'overview' }],
  },
  {
    // 图纸页就是管理图纸（CR-044，客户原话「其实图纸就是你说的管理图纸，对不」）：
    // 和网页端同一页，上传、加删图钉按 drawing.upload 出现，只看的人看得到图钉内容。
    // 改之前是只读的「资料」页 + 藏在首页「其他」里的「管理图纸」，admin 都没找到
    id: 'm.drawings',
    shell: 'mobile',
    path: '/m/drawings',
    siteScoped: false,
    twin: 'd.drawings',
    parent: 'm.home',
    variants: [DRAWINGS_ADMIN],
    entries: [{ slot: 'homeCard', order: 30, card: 'drawings' }],
  },
  {
    // 看和换是同一页（CR-044）：「换一张」按 schedule.upload 出现。改之前这条路径只给能换的人，看在「资料」页顶上
    id: 'm.schedule',
    shell: 'mobile',
    path: '/m/schedule',
    siteScoped: false,
    twin: 'd.schedule',
    parent: 'm.home',
    variants: [SCHEDULE_PAGE],
    entries: [{ slot: 'homeCard', order: 31, card: 'schedule' }],
  },
  {
    id: 'm.attendance',
    shell: 'mobile',
    path: '/m/attendance',
    siteScoped: false,
    twin: 'd.attendance',
    parent: 'm.home',
    variants: [ATTENDANCE_PAGE],
    entries: [{ slot: 'homeCard', order: 40, card: 'attendance' }],
  },
  {
    id: 'm.me',
    shell: 'mobile',
    path: '/m/me',
    siteScoped: false,
    variants: [{ gate: {}, page: 'MePage', loads: [] }],
    entries: [], // 底部栏固定的「我的」
  },
  {
    id: 'm.system',
    shell: 'mobile',
    path: '/m/settings',
    parent: 'm.me',
    siteScoped: false,
    // 门槛 = 下面几项入口的并集：进得来就至少有一项（单位页只给超管，超管隐式全有，不用单列）
    variants: [
      {
        gate: { anyOf: [P.SITE_MANAGE, P.SITE_CREATE, P.USER_MANAGE, P.ROLE_MANAGE, P.AUDIT_VIEW] },
        page: 'SystemPage',
        loads: [],
      },
    ],
    entries: [{ slot: 'meMenu', order: 10 }],
  },
  {
    id: 'm.siteSettings',
    shell: 'mobile',
    path: '/m/settings/site',
    parent: 'm.system',
    siteScoped: false,
    twin: 'd.siteSettings',
    variants: [SITE_SETTINGS],
    entries: [{ slot: 'systemHub', order: 10 }],
  },
  {
    id: 'm.sites',
    shell: 'mobile',
    path: '/m/settings/sites',
    parent: 'm.system',
    siteScoped: false,
    twin: 'd.sites',
    variants: [{ ...SITES_PAGE, gate: { anyOf: [P.SITE_CREATE, P.SITE_MANAGE] } }],
    entries: [{ slot: 'systemHub', order: 20 }],
  },
  {
    id: 'm.users',
    shell: 'mobile',
    path: '/m/settings/users',
    parent: 'm.system',
    siteScoped: false,
    twin: 'd.users',
    variants: [USERS_PAGE],
    entries: [{ slot: 'systemHub', order: 30 }],
  },
  {
    id: 'm.roles',
    shell: 'mobile',
    path: '/m/settings/roles',
    parent: 'm.system',
    siteScoped: false,
    twin: 'd.roles',
    variants: [ROLES_PAGE],
    entries: [{ slot: 'systemHub', order: 40 }],
  },
  {
    id: 'm.audit',
    shell: 'mobile',
    path: '/m/settings/audit',
    parent: 'm.system',
    siteScoped: false,
    twin: 'd.audit',
    variants: [AUDIT_PAGE],
    entries: [{ slot: 'systemHub', order: 50 }],
  },
  {
    id: 'm.orgs',
    shell: 'mobile',
    path: '/m/settings/orgs',
    parent: 'm.system',
    siteScoped: false,
    twin: 'd.orgs',
    variants: [ORGS_PAGE],
    entries: [{ slot: 'systemHub', order: 5 }],
  },
];

export const ROUTE_BY_ID = new Map(ROUTES.map((route) => [route.id, route]));
